import { Check } from "lucide-react";
import { useTheme } from "../theme/ThemeContext";
import { LOGO_VARIANTS } from "../components/Logo";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";

const PALETTES = [
  {
    id: "salvia",
    name: "Salvia & Terracotta",
    tag: "Scelta definitiva",
    swatches: ["#557061", "#C68A6D", "#C7A671", "#FAF6F1", "#2C352F"],
    why: "Verde salvia = natura, equilibrio, fiducia. Terracotta = calore mediterraneo, azione. Champagne = tocco premium. È la palette più 'scientifica e seria': comunica competenza e tranquillità.",
    mood: "Professionale · Naturale · Rassicurante",
  },
  {
    id: "cipria",
    name: "Cipria & Prugna",
    tag: "Alternativa 1",
    swatches: ["#6B4A52", "#C97B84", "#D6AE96", "#FBF6F4", "#3A2E31"],
    why: "Rosa cipria = cura, dolcezza, vicinanza emotiva. Prugna profonda = eleganza ed esperienza (evita l'effetto 'troppo giovane'). È la palette più 'umana e femminile': parla a chi cerca accompagnamento senza giudizio.",
    mood: "Caldo · Elegante · Empatico",
  },
  {
    id: "acqua",
    name: "Verde Acqua & Corallo",
    tag: "Alternativa 2",
    swatches: ["#2F6B66", "#D98C73", "#9FD1CA", "#F7FAF8", "#1F3331"],
    why: "Verde acqua/teal = freschezza, pulizia, scienza e laboratorio (perfetto per il tema microbiota). Corallo = energia e calore umano che bilancia la freddezza. È la palette più 'giovane e moderna'.",
    mood: "Fresco · Moderno · Scientifico",
  },
];

const Swatches = ({ colors }) => (
  <div className="flex gap-1.5">
    {colors.map((c) => (
      <span key={c} className="h-8 w-8 rounded-full border border-black/10" style={{ background: c }} title={c} />
    ))}
  </div>
);

export default function Style() {
  const { theme, setTheme, logo, setLogo } = useTheme();

  return (
    <main className="pt-28 pb-24 sm:pt-36" data-testid="style-page">
      <Seo title="Palette & Logo" description="Anteprima interna" noindex />
      <div className="max-w-5xl mx-auto px-5">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.22em] text-clay font-semibold mb-3">Anteprima interna</p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl text-ink tracking-tight" data-testid="style-title">Palette & Logo</h1>
          <p className="mt-4 text-muted max-w-2xl">
            Scegli una palette o un logo: si applica subito a tutto il sito (solo su questo dispositivo) così puoi navigare e confrontare.
            Quando decidi, dimmelo e lo fisso come definitivo.
          </p>
        </Reveal>

        <section className="mt-14" data-testid="palette-section">
          <h2 className="font-serif text-2xl text-ink mb-6">1. Palette colori</h2>
          <div className="grid gap-5 md:grid-cols-3">
            {PALETTES.map((p) => {
              const active = theme === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`palette-${p.id}`}
                  onClick={() => setTheme(p.id)}
                  className={`text-left rounded-3xl border bg-white p-6 transition-all duration-300 hover:shadow-lg ${active ? "border-pine ring-2 ring-pine/20" : "border-border"}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[11px] font-mono uppercase tracking-widest text-sage">{p.tag}</span>
                    {active && <span className="inline-flex items-center gap-1 text-xs font-semibold text-pine"><Check size={14} /> In uso</span>}
                  </div>
                  <Swatches colors={p.swatches} />
                  <h3 className="font-serif text-xl text-ink mt-5">{p.name}</h3>
                  <p className="text-xs font-mono uppercase tracking-wider text-clay mt-1">{p.mood}</p>
                  <p className="text-sm text-muted mt-3 leading-relaxed">{p.why}</p>
                </button>
              );
            })}
          </div>
        </section>

        <section className="mt-16" data-testid="logo-section">
          <h2 className="font-serif text-2xl text-ink mb-6">2. Logo</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {LOGO_VARIANTS.map((v) => {
              const active = logo === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  data-testid={`logo-${v.id}`}
                  onClick={() => setLogo(v.id)}
                  className={`text-left rounded-3xl border bg-white p-6 transition-all duration-300 hover:shadow-lg ${active ? "border-pine ring-2 ring-pine/20" : "border-border"}`}
                >
                  <div className="flex items-center justify-between gap-4 mb-5">
                    <div className="rounded-2xl bg-sand p-4"><v.Component className="h-16 w-16" /></div>
                    <div className="rounded-2xl bg-pine-dark p-4"><v.Component className="h-16 w-16" dark /></div>
                  </div>
                  <div className="flex items-center gap-2.5 mb-3">
                    <v.Component className="h-7 w-7" />
                    <span className="font-serif font-semibold text-base text-pine">Gusto <span className="italic text-clay">y</span> Ciencia</span>
                  </div>
                  <h3 className="font-serif text-lg text-ink flex items-center gap-2">
                    {v.name} {active && <Check size={16} className="text-pine" />}
                  </h3>
                  <p className="text-sm text-muted mt-2 leading-relaxed">{v.desc}</p>
                </button>
              );
            })}
          </div>
        </section>

        <section className="mt-16 rounded-3xl bg-pine text-linen p-8 sm:p-10" data-testid="style-preview">
          <p className="text-xs font-mono uppercase tracking-[0.22em] text-gold mb-3">Anteprima combinata</p>
          <h3 className="font-serif text-3xl sm:text-4xl">Ciencia en el plato, bienestar en el alma.</h3>
          <p className="mt-4 text-linen/80 max-w-xl">Così appaiono titoli, testo e pulsante con la palette scelta.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <span className="rounded-full bg-clay px-5 py-2.5 text-sm font-semibold text-white">Prenota la prima consulenza</span>
            <span className="rounded-full border border-linen/40 px-5 py-2.5 text-sm font-semibold">Scopri i servizi</span>
          </div>
        </section>
      </div>
    </main>
  );
}
